import { useState } from 'react';
import { C } from '@ridgewill/brand';
import type { Routing } from '../lib/fleet-api.js';
import {
  actorName,
  auditView,
  blacksmithMeter,
  blacksmithNote,
  boxActionOf,
  changeText,
  githubMeter,
  githubNote,
  hostedTier,
  labelName,
  routingStatus,
  switchCopy,
  targetOf,
  TARGETS,
  type RunnerTarget,
} from '../lib/fleet-routing.js';
import { usd } from '../lib/fleet-view.js';
import { count, numeric } from '../lib/format.js';
import { useAction, useAsync } from '../lib/useAsync.js';
import { usePoll } from '../lib/usePoll.js';
import { useSession } from '../lib/auth-client.js';
import { AsyncBoundary, Badge, Button, DotPill, EmptyState, Note, OutcomeNote, Panel, Table, Td, When } from '../components/ui.js';
import { InfoBubble } from '../components/Screen.js';
import { Modal, ModalActions } from '../components/Modal.js';

/**
 * Where CI runs, and moving it by hand. org-admin#98.
 *
 * fleet-api moves the `CI_RUNNER_LABEL` organisation variable on its own
 * (fleet-api#21); this section shows that state, both month-to-date meters and
 * every change, and takes a manual switch through fleet-api so it is audited
 * with the admin's id and gets the same checks as the cron.
 *
 * ── A SWITCH IS A MODAL, NOT A HOLD ──────────────────────────────────
 *
 * It moves every auth and keyodo job, which earns a confirmation; it is also
 * reversible in one more switch, which is why it is not a HoldButton. The
 * confirmation's job is to say what happens that is not obvious: jobs already
 * queued stay put, and a switch away pauses automatic routing.
 */
export function CiRouting({ load, setLabel }: { load: () => Promise<Routing>; setLabel: (label: string) => Promise<Routing> }) {
  const routing = useAsync(load);
  usePoll(routing.reload);
  const { data: session } = useSession();
  const [choosing, setChoosing] = useState<RunnerTarget | null>(null);
  const [moved, setMoved] = useState<string | null>(null);

  return (
    <AsyncBoundary state={routing.state}>
      {(r) => (
        <div style={{ display: 'grid', gap: '1rem' }} className="rw-fade">
          {moved ? <OutcomeNote outcome={{ ok: true, message: moved }} /> : null}
          <WhereCiRuns routing={r} onChoose={(t) => (setMoved(null), setChoosing(t))} />
          <Meters routing={r} />
          {/* `user?.`: the portal only mounts this after a real admin sign-in, but a
              session answer without a user (an auth origin misconfigured) must
              cost the "You" label, not the whole section. */}
          <History routing={r} currentUserId={session?.user?.id ?? null} />
          {choosing ? (
            <ConfirmSwitch
              routing={r}
              to={choosing}
              setLabel={setLabel}
              onClose={() => setChoosing(null)}
              onDone={(message) => {
                setChoosing(null);
                setMoved(message);
                routing.reload(true);
              }}
            />
          ) : null}
        </div>
      )}
    </AsyncBoundary>
  );
}

function WhereCiRuns({ routing: r, onChoose }: { routing: Routing; onChoose: (target: RunnerTarget) => void }) {
  const status = routingStatus(r);
  const current = targetOf(r.state.label, r.chain);
  const away = current !== null && current !== 'fleet';
  const trialDue = r.mode === 'auto' && away && !r.state.paused_reason && r.state.next_trial_at;
  const hosted = hostedTier(r);
  // Automatic's detail is the rule itself, which the info bubble explains.
  // Every other status's detail is news, and stays on the page.
  const news = status.tone === 'good' ? null : status.detail;
  const trip = count(r.blacksmith.trip_min);

  return (
    <Panel
      title="Where CI runs"
      action={<DotPill tone={status.tone}>{status.label}</DotPill>}
      info={
        <InfoBubble label="About routing">
          <p style={{ margin: 0 }}>
            <strong>Busy repos</strong> read the <code>CI_RUNNER_LABEL</code> organisation variable. If a job waits{' '}
            {count(Math.round(r.failover_after_s / 60))} min with none of your runners starting work, fleet-api moves them to
            Blacksmith and re-runs the jobs left waiting, or to GitHub-hosted runners past {trip} Blacksmith minutes. They
            come back once your runners have been up for {count(Math.round(r.failback_after_s / 60))} min.
          </p>
          <p style={{ margin: '.6rem 0 0' }}>
            <strong>Low-churn repos</strong> read <code>CI_HOSTED_LABEL</code>. They spend Blacksmith&apos;s free minutes
            first, move to GitHub-hosted runners at {trip} Blacksmith minutes, and return to Blacksmith when the month
            resets.
          </p>
          <p style={{ margin: '.6rem 0 0' }}>
            Choosing a card moves the busy repos by hand. Away from your runners, that pauses automatic routing until they
            are moved back.
          </p>
        </InfoBubble>
      }
    >
      {r.state.label !== null && current === null ? (
        <div style={{ padding: '1rem 1.1rem 0' }}>
          <Note tone="warn">
            CI_RUNNER_LABEL is {labelName(r.state.label, r.chain)}, which is none of these. Choose one to put CI back on a
            known runner.
          </Note>
        </div>
      ) : null}
      <div style={s.targets} role="group" aria-label="Where CI runs">
        {TARGETS.map((t) => {
          const active = t.target === current;
          const lowChurn = hosted?.target === t.target;
          return (
            <button
              key={t.target}
              type="button"
              onClick={() => onChoose(t.target)}
              disabled={active}
              aria-pressed={active}
              style={{ ...s.target, ...(active ? s.targetActive : null) }}
              className={active ? undefined : 'rw-press'}
            >
              <span style={s.targetHead}>
                <span style={s.targetName}>{t.name}</span>
                <span style={s.tags}>
                  {active ? <span style={{ ...s.tag, ...s.tagBusy }}>Busy repos</span> : null}
                  {lowChurn ? <span style={{ ...s.tag, ...s.tagLow }}>Low-churn</span> : null}
                </span>
              </span>
              <code style={s.targetLabel}>{r.chain[t.target]}</code>
              {active ? null : <span style={s.targetHint}>Move busy repos here</span>}
            </button>
          );
        })}
      </div>
      <div style={s.footnote}>
        {news ? <p style={s.news}>{news}</p> : null}
        {trialDue ? (
          <p style={s.news}>
            Tries your runners again at <span style={numeric}>{clock(r.state.next_trial_at!)}</span>.
          </p>
        ) : null}
        <dl style={s.tiers}>
          <div style={s.tier}>
            <dt style={{ ...s.tag, ...s.tagBusy }}>Busy repos</dt>
            <dd style={s.tierText}>
              {labelName(r.state.label, r.chain)}
              {r.state.switched_at ? (
                <>
                  {' · changed '}
                  <When iso={r.state.switched_at} /> {changedBy(r.state.switched_by)}
                </>
              ) : null}
            </dd>
          </div>
          {hosted ? (
            <div style={s.tier}>
              <dt style={{ ...s.tag, ...s.tagLow }}>Low-churn</dt>
              <dd style={s.tierText}>
                {hosted.name}
                {r.hosted?.set_at ? (
                  <>
                    {' · moved '}
                    <When iso={r.hosted.set_at} />
                  </>
                ) : null}
                {` · ${hosted.next}`}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
    </Panel>
  );
}

/** A bar with room above and below it for the trip-line marker to stand proud of the track. */
const meter = {
  track: { position: 'relative' as const, height: 16, display: 'flex', alignItems: 'center' },
  bar: { display: 'flex', width: '100%', height: 8, borderRadius: 4, background: C.page, border: `1px solid ${C.border}`, overflow: 'hidden' },
  marker: { position: 'absolute' as const, top: 0, bottom: 0, width: 2, borderRadius: 1, background: C.textMid },
};

function Meters({ routing: r }: { routing: Routing }) {
  const b = r.blacksmith;
  const bm = blacksmithMeter(b);
  const gh = r.github_hosted;
  const limit = r.github_limit;
  const gm = githubMeter(gh ? gh.gross_min : null, limit);
  const bn = blacksmithNote(b);
  return (
    <Panel
      title="This month"
      info={
        <InfoBubble label="About these meters">
          <p style={{ margin: 0 }}>
            <strong>Blacksmith</strong> gives {count(b.free_min)} free minutes each calendar month, then bills every minute and
            keeps running jobs. The marker is {count(b.trip_min)}: past it, routing stops choosing Blacksmith and the low-churn
            repos move to GitHub-hosted runners.
          </p>
          <p style={{ margin: '.6rem 0 0' }}>
            <strong>GitHub-hosted</strong> includes {count(limit.included_min)} minutes in the plan
            {gm.capped && limit.budget_usd !== null ? `, then paid minutes up to the ${usd(limit.budget_usd)} budget, where GitHub stops Actions` : ''}.
            These figures come from GitHub&apos;s bill, which fleet-api reads once an hour.
          </p>
        </InfoBubble>
      }
    >
      <div style={s.meters}>
        <div style={s.meter}>
          <div style={s.meterHead}>
            <span>Blacksmith</span>
            <span style={numeric}>
              {count(b.used_min)} of {count(b.free_min)} min
            </span>
          </div>
          <div
            style={meter.track}
            role="img"
            aria-label={`${count(b.used_min)} of ${count(b.free_min)} free Blacksmith minutes used. Automatic routing moves CI to GitHub-hosted runners at ${count(b.trip_min)}.`}
          >
            <div style={meter.bar}>
              <div style={{ width: `${bm.okPct}%`, background: C.successDot }} />
              <div style={{ width: `${bm.warnPct}%`, background: C.warnDot }} />
            </div>
            <div
              title={`${count(b.trip_min)} min: automatic routing moves CI to GitHub-hosted runners`}
              style={{ ...meter.marker, left: `calc(${bm.markerPct}% - 1px)` }}
            />
          </div>
          <div style={s.meterFoot}>
            <span style={bn.warn ? s.footWarn : undefined}>{bn.text}</span>
            <span style={numeric}>Trip line {count(b.trip_min)}</span>
          </div>
        </div>
        <div style={s.meter}>
          <div style={s.meterHead}>
            <span>GitHub-hosted</span>
            <span style={numeric}>{gh ? `${count(gh.gross_min)} of ${count(gm.maxMin)} min` : 'Not read yet'}</span>
          </div>
          <div
            style={meter.track}
            role="img"
            aria-label={gh ? `${count(gh.gross_min)} of ${count(gm.maxMin)} GitHub-hosted minutes used this month.` : 'GitHub-hosted minutes not read yet.'}
          >
            <div style={meter.bar}>
              <div style={{ width: `${gm.pct}%`, background: C.successDot }} />
            </div>
          </div>
          <div style={s.meterFoot}>
            {gh ? (
              <>
                <span>{githubNote(gh, limit)}</span>
                <span>
                  Bill read <When iso={gh.as_of} />
                </span>
              </>
            ) : (
              <span>Read from GitHub&apos;s bill once an hour</span>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
}

function History({ routing: r, currentUserId }: { routing: Routing; currentUserId: string | null }) {
  return (
    <Panel
      title="Routing history"
      info={
        <InfoBubble label="About routing history">
          <p style={{ margin: 0 }}>The last 50 changes, would-be changes, refusals and box actions, newest first.</p>
        </InfoBubble>
      }
    >
      {r.audit.length === 0 ? (
        <EmptyState title="Nothing has changed yet" suggestion="A switch, by fleet-api or from here, appears as soon as it happens." />
      ) : (
        <Table columns={['When', 'Change', 'Why', 'By', 'Result']}>
          {r.audit.map((row) => {
            const view = auditView(row);
            return (
              <tr key={row.id}>
                <Td>
                  <When iso={row.at} />
                </Td>
                <Td>{changeText(row, r.chain)}</Td>
                <Td>
                  <span style={{ color: C.textMid }}>{row.reason}</span>
                </Td>
                <Td>{actorName(row.actor, currentUserId, boxActionOf(row)?.box)}</Td>
                <Td>
                  <Badge tone={view.tone}>{view.label}</Badge>
                </Td>
              </tr>
            );
          })}
        </Table>
      )}
    </Panel>
  );
}

function ConfirmSwitch({
  routing: r,
  to,
  setLabel,
  onClose,
  onDone,
}: {
  routing: Routing;
  to: RunnerTarget;
  setLabel: (label: string) => Promise<Routing>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const action = useAction();
  const copy = switchCopy(to, r);
  const confirm = () => {
    let moved = false;
    void action
      .run(async () => {
        await setLabel(r.chain[to]);
        moved = true;
        return copy.done;
      })
      // `run` settles either way; only a switch that happened closes the dialog,
      // so a refusal stays on screen next to the button that caused it.
      .then(() => {
        if (moved) onDone(copy.done);
      });
  };

  return (
    <Modal
      title={copy.title}
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button disabled={action.running} onClick={confirm}>
            {action.running ? 'Moving...' : copy.button}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <ul style={s.points}>
        {copy.points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </Modal>
  );
}

const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** How the busy repos' label last changed, in a few words after "changed 2d ago". */
function changedBy(by: string | null): string {
  switch (by) {
    case 'fleet-api':
      return 'automatically';
    case 'portal':
      return 'from this portal';
    case 'github':
      return 'outside fleet-api';
    case 'first_reading':
      return 'on first reading';
    default:
      return 'by an unknown change';
  }
}

const s = {
  targets: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '.75rem',
    padding: '1rem 1.1rem',
  },
  target: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    gap: '.2rem',
    padding: '.75rem .9rem .8rem',
    textAlign: 'left' as const,
    font: 'inherit',
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.inputBorder}`,
    borderRadius: 10,
    cursor: 'pointer',
  },
  targetActive: {
    background: C.page,
    border: `2px solid ${C.brand}`,
    // One pixel less padding on every side, so the thicker border does not
    // make the chosen card taller than its neighbours.
    padding: 'calc(.75rem - 1px) calc(.9rem - 1px) calc(.8rem - 1px)',
    cursor: 'default',
  },
  targetHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.5rem', alignSelf: 'stretch', flexWrap: 'wrap' as const },
  targetName: { fontSize: '.95rem', fontWeight: 680 },
  targetLabel: { fontSize: '.75rem', color: C.muted, overflowWrap: 'anywhere' as const },
  targetHint: { fontSize: '.78rem', fontWeight: 600, color: C.muted, marginTop: '.25rem' },
  tags: { display: 'inline-flex', gap: '.35rem', flexWrap: 'wrap' as const },
  /** The two tiers' tags, on the cards and beside the lines under them, so the two read as one key. */
  tag: {
    display: 'inline-block',
    margin: 0,
    padding: '.08rem .5rem',
    borderRadius: 999,
    fontSize: '.7rem',
    fontWeight: 650,
    letterSpacing: '.02em',
    lineHeight: 1.5,
    whiteSpace: 'nowrap' as const,
  },
  tagBusy: { background: C.blueTint, color: C.blueText, border: `1px solid ${C.blueTint}` },
  tagLow: { background: C.surface, color: C.textMid, border: `1px solid ${C.border}` },
  footnote: {
    display: 'grid',
    gap: '.55rem',
    padding: '0 1.1rem 1.1rem',
    fontSize: '.82rem',
    lineHeight: 1.5,
    color: C.muted,
  },
  news: { margin: 0, color: C.textMid },
  tiers: { display: 'grid', gap: '.4rem', margin: 0 },
  tier: { display: 'flex', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap' as const },
  tierText: { margin: 0, color: C.textMid },
  meters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '1rem 1.5rem',
    padding: '1rem 1.1rem 1.1rem',
  },
  meter: { display: 'grid', gap: '.35rem', alignContent: 'start' },
  meterHead: { display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '.85rem', fontWeight: 600, color: C.textMid },
  meterFoot: { display: 'flex', justifyContent: 'space-between', gap: '.5rem 1rem', flexWrap: 'wrap' as const, fontSize: '.78rem', color: C.muted },
  footWarn: { color: C.warnText, fontWeight: 600 },
  points: { margin: 0, paddingLeft: '1.1rem', display: 'grid', gap: '.45rem', lineHeight: 1.5, fontSize: '.88rem' },
};
